import React, { useState, useEffect } from 'react';
import Layout from '../../layout/Layout';
import { userSettingsApi } from '../../../api/usersettings';

const ApiKeysPage = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [newKeyDescription, setNewKeyDescription] = useState('');
  const [newlyCreatedKey, setNewlyCreatedKey] = useState('');
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const fetchApiKeys = async () => {
    try {
      const keys = await userSettingsApi.listApiKeys();
      setApiKeys(keys || []);
    } catch (error) {
      showNotification('Failed to load API keys', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateKey = async (e) => {
    e.preventDefault();
    try {
      const response = await userSettingsApi.createApiKey(newKeyDescription);
      setApiKeys([...apiKeys, {
        id: response.id,
        description: newKeyDescription,
        created_at: response.created_at,
        expires_at: response.expires_at,
        last_used_at: null
      }]);
      setNewKeyDescription('');
      setIsCreateModalOpen(false);
      setNewlyCreatedKey(response.api_key);
      setIsSuccessModalOpen(true);
    } catch (error) {
      showNotification('Failed to create API key', 'error');
    }
  };

  const handleCopyKey = () => {
    navigator.clipboard.writeText(newlyCreatedKey).then(() => {
      showNotification('API key copied to clipboard', 'success');
    });
  };

  const handleCloseSuccessModal = () => {
    setIsSuccessModalOpen(false);
    setNewlyCreatedKey('');
    showNotification('API key created successfully', 'success');
  };

  const handleDeleteKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to delete this API key? This action cannot be undone.')) {
      return;
    }

    try {
      await userSettingsApi.deleteApiKey(keyId);
      setApiKeys(apiKeys.filter(key => key.id !== keyId));
      showNotification('API key deleted successfully', 'success');
    } catch (error) {
      showNotification('Failed to delete API key', 'error');
    }
  };

  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification({ show: false, message: '', type: '' }), 5000);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold">API Keys</h1>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
          >
            Create new secret key
          </button>
        </div>

        {notification.show && (
          <div
            className={`mb-6 p-4 rounded-md ${
              notification.type === 'error'
                ? 'bg-red-100 text-red-700 border border-red-200'
                : 'bg-green-100 text-green-700 border border-green-200'
            }`}
          >
            {notification.message}
          </div>
        )}

        <div className="bg-gray-900 rounded-lg shadow-lg overflow-hidden">
          <div className="p-6">
            <p className="text-gray-300 mb-4">
              As an owner of this organization, you can view and manage all API keys in this organization.
            </p>
            <p className="text-gray-300 mb-4">
              Do not share your API key with others or expose it in the browser or other client-side code.
              To protect your account's security, we may automatically disable any API key that has leaked publicly.
            </p>
          </div>

          {isLoading ? (
            <div className="p-6 text-center text-gray-300">Loading API keys...</div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Description</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Last Used</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Expires</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-gray-900 divide-y divide-gray-800">
                {apiKeys.map((key) => (
                  <tr key={key.id}>
                    <td className="px-6 py-4 text-gray-300">{key.description || 'Unnamed Key'}</td>
                    <td className="px-6 py-4 text-gray-300">{formatDate(key.created_at)}</td>
                    <td className="px-6 py-4 text-gray-300">{formatDate(key.last_used_at)}</td>
                    <td className="px-6 py-4 text-gray-300">{formatDate(key.expires_at)}</td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handleDeleteKey(key.id)}
                        className="text-red-400 hover:text-red-500"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
                {apiKeys.length === 0 && (
                  <tr>
                    <td colSpan="5" className="px-6 py-4 text-center text-gray-300">
                      No API keys found. Create one to get started.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* Create Key Modal */}
        {isCreateModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-gray-900 rounded-lg p-6 w-full max-w-md">
              <h2 className="text-xl font-semibold text-white mb-4">Create new secret key</h2>
              <form onSubmit={handleCreateKey}>
                <div className="mb-4">
                  <label className="block text-gray-300 text-sm font-medium mb-2">
                    Description (optional)
                  </label>
                  <input
                    type="text"
                    value={newKeyDescription}
                    onChange={(e) => setNewKeyDescription(e.target.value)}
                    placeholder="My API Key"
                    className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={() => setIsCreateModalOpen(false)}
                    className="px-4 py-2 text-gray-300 hover:text-white"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                  >
                    Create secret key
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Success Modal with API Key */}
        {isSuccessModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="bg-gray-900 rounded-lg p-6 w-full max-w-md">
              <h2 className="text-xl font-semibold text-white mb-4">Save your API key</h2>
              <div className="mb-4">
                <p className="text-gray-300 mb-4">
                  Make sure to copy your API key now. You won't be able to see it again!
                </p>
                <div className="bg-gray-800 p-4 rounded-md flex items-center space-x-2">
                  <code className="text-green-400 flex-1 font-mono break-all">
                    {newlyCreatedKey}
                  </code>
                  <button
                    onClick={handleCopyKey}
                    className="px-3 py-1 bg-gray-700 text-gray-300 rounded hover:bg-gray-600"
                  >
                    Copy
                  </button>
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  onClick={handleCloseSuccessModal}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                >
                  I've saved my API key
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default ApiKeysPage; 